import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { InputText } from "primereact/inputtext";
import React, { useEffect, useState } from "react";
import { Button } from "react-bootstrap";
import * as Icon from "react-bootstrap-icons";
import { useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { City } from "../../models/cities.model";
import { citiesService } from "../../service/cities.service";

const initialCity: City = {
  name: "",
  stateId: 0,
};

const CityComponent: React.FC = () => {
  const [cities, setCities] = useState<City[]>([]);
  const [allCities, setAllCities] = useState<City[]>([]);
  const [selectedCityFilter, setSelectedCityFilter] = useState<City | null>(
    null
  );
  const [city, setCity] = useState<City>(initialCity);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const { stateName, countryName, stateIdFromState } = location.state || {};
  const [selectedState, setSelectedState] = useState<number | null>(null);
  const [globalFilter, setGlobalFilter] = useState("");
  const [listLoading, setListLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(() => {
    const savedPage = localStorage.getItem("cityTableFirst");
    return savedPage ? parseInt(savedPage) : 0;
  });
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const savedRows = localStorage.getItem("cityTableRows");
    return savedRows ? parseInt(savedRows) : 10;
  });

  useEffect(() => {
    const handleStateChange = async (stateId: number) => {
      setSelectedState(stateId);
      setListLoading(true);
      try {
        const result = await citiesService.getByStateId(stateId);
        const citiesData = result ?? [];
        setCities(citiesData);
        setAllCities(citiesData);
      } catch {
        setCities([]);
        setAllCities([]);
      } finally {
        setListLoading(false);
      }
    };

    if (stateIdFromState) {
      handleStateChange(stateIdFromState);
    }
  }, [stateIdFromState]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCity((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...city, stateId: stateIdFromState ?? city.stateId };
      if (city.id) {
        const updated = await citiesService.update(city.id, payload);
        setCities((prev) => prev.map((c) => (c.id === city.id ? updated : c)));
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "success",
          title: "City updated successfully.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      } else {
        const created = await citiesService.create(payload);
        setCities((prev) => [...prev, created]);
        Swal.fire({
          toast: true,
          position: "bottom",
          icon: "success",
          title: "City created successfully.",
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
        });
      }
      setCity(initialCity);
      setShowCreateForm(false);
    } catch (err: any) {
      Swal.fire({
        toast: true,
        position: "bottom",
        icon: "error",
        title: "Failed to save city.",
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (rowData: City) => {
    setCity(rowData);
    setSelectedState(rowData.stateId);
    setShowCreateForm(true);
  };

  const handleDelete = async (rowData: City) => {
    if (!rowData.id) return;
    try {
      await citiesService.delete(rowData.id);
      setCities((prev) => prev.filter((c) => c.id !== rowData.id));
      Swal.fire("Deleted", "City deleted successfully", "success");
    } catch {
      Swal.fire("Error", "Failed to delete city", "error");
    }
  };

  const actionBodyTemplate = (rowData: City) => (
    <div className="d-flex align-items-center gap-2">
      <Button
        variant="link"
        className="p-0"
        onClick={() => handleEdit(rowData)}
        title="Edit"
        style={{ color: "#198754" }}
      >
        <Icon.PencilSquare size={20} />
      </Button>
      {/* <Button variant="link" className="p-0" onClick={() => handleDelete(rowData)} title="Delete" style={{ color: '#dc3545' }}>
        <Icon.Trash size={20} />
      </Button> */}
    </div>
  );

  return (
    <div className="content has-page-header">
      <div className="content-header mb-4">
        <h2>{`Cities under - ${stateName} / ${countryName}`}</h2>
      </div>
      <div className="content-body">
        <div className="container mt-4">
          <div className="row shadow">
            <div className="col-md-12 mb-3">
              <div className="d-flex align-items-center justify-content-between gap-3 my-3">
                <IconField iconPosition="left" style={{ width: "200px" }}>
                  <InputIcon>
                    <Icon.Search className="tx-12" />
                  </InputIcon>
                  <InputText
                    value={globalFilter}
                    onChange={(e) => {
                      setGlobalFilter(e.target.value);
                      setSelectedCityFilter(null);
                      setCurrentPage(0);
                      localStorage.setItem("cityTableFirst", "0");
                    }}
                    placeholder="Search"
                    style={{ height: "38px" }}
                  />
                </IconField>
                <div className="d-flex align-items-center gap-3">
                  <Dropdown
                    value={selectedCityFilter}
                    onChange={(e: { value: City | null }) => {
                      setSelectedCityFilter(e.value);
                      setCurrentPage(0);
                      setGlobalFilter("");
                    }}
                    options={allCities}
                    optionLabel="name"
                    placeholder="Select City"
                    style={{ width: "200px", height: "38px" }}
                    appendTo="self"
                    showClear
                    filter
                    filterBy="name"
                  />
                  <Button
                    className="btn btn-primary"
                    style={{ height: "38px", width: "200px" }}
                    onClick={() => {
                      setCity(initialCity);
                      setShowCreateForm(true);
                    }}
                  >
                    <Icon.PlusCircle className="me-1" /> Create City
                  </Button>
                </div>
              </div>

              <DataTable
                value={
                  selectedCityFilter
                    ? cities.filter((c) => c.id === selectedCityFilter.id)
                    : globalFilter
                    ? cities.filter((c) =>
                        c.name
                          .toLowerCase()
                          .includes(globalFilter.toLowerCase())
                      )
                    : cities
                }
                dataKey="id"
                paginator
                rows={rowsPerPage}
                first={currentPage}
                onPage={(e) => {
                  setCurrentPage(e.first);
                  setRowsPerPage(e.rows);
                  localStorage.setItem("cityTableFirst", e.first.toString());
                  localStorage.setItem("cityTableRows", e.rows.toString());
                }}
                loading={listLoading}
                rowsPerPageOptions={[5, 10, 20]}
                stripedRows
                sortMode="single"
                emptyMessage={
                  selectedState
                    ? "No cities found for the selected state."
                    : "Please select a country and state to load cities."
                }
                className="grid-table"
              >
                <Column field="name" header="Name" sortable />
                {/* <Column field="stateId" header="State ID" sortable /> */}
                <Column
                  header="Actions"
                  body={actionBodyTemplate}
                  style={{ minWidth: "120px" }}
                />
              </DataTable>

              {showCreateForm && (
                <div
                  className="modal show d-block"
                  tabIndex={-1}
                  style={{ background: "rgba(0,0,0,0.3)" }}
                >
                  <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">
                          {city.id ? "Update City" : "Create City"}
                        </h5>
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => setShowCreateForm(false)}
                        ></button>
                      </div>
                      <form onSubmit={handleSubmit}>
                        <div className="modal-body">
                          <div className="mb-3">
                            <label className="form-label">Name</label>
                            <input
                              className="form-control"
                              name="name"
                              value={city.name}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        </div>
                        <div className="modal-footer">
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                          >
                            {loading
                              ? city.id
                                ? "Updating..."
                                : "Creating..."
                              : city.id
                              ? "Update"
                              : "Create"}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setShowCreateForm(false)}
                          >
                            Cancel
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CityComponent;
